import { v4 as uuidv4 } from 'uuid'
import { useContext, useEffect, useRef, useState } from 'react'
import './BookmarkChat.scss'
import useBookmarks, {
  GeneratingBookmarkStatus,
} from 'containers/embed/useBookmarks'
import ScreenLoading from 'components/ScreenLoading'
import { BookmarksContext } from 'containers/bookmarks/BookmarksContext'
import { Bookmark } from 'types'

type Message = {
  id: string
  status: 'error' | 'info'
  message: string
  type: 'bot' | 'user'
  action?: 'apply'
  bookmark?: Bookmark
}

const BookmarkChat = ({ reportSlug, organizationId }) => {
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [currentMessage, setCurrentMessage] = useState('')
  const messageInputRef = useRef<HTMLInputElement>(null)
  const {
    generateBookmark,
    generatingBookmarkStatus,
    setGeneratingBookmarkStatus,
    bookmarkError,
    setBookmarkError,
  } = useBookmarks(organizationId)
  const { setActiveBookmark, setFetchedBookmarks } =
    useContext(BookmarksContext)

  const openChat = () => {
    setIsChatOpen((prev) => !prev)
  }

  const sendMessage = async () => {
    if (currentMessage.length === 0) {
      return
    }

    const message = currentMessage
    setCurrentMessage('')
    setMessages((prev) => [
      ...prev,
      { id: uuidv4(), message: message, type: 'user' } as Message,
    ])

    try {
      const response = await generateBookmark(reportSlug, message)
      if (response?.success) {
        setFetchedBookmarks((prevBookmarks) => [
          ...prevBookmarks,
          response.bookmark,
        ])
        const applyMessage: Message = {
          id: uuidv4(),
          status: 'info',
          type: 'bot',
          message: `Successfully created bookmark '${response.bookmark.name}' with prompt '${message}'.`,
          action: 'apply',
          bookmark: response.bookmark,
        }
        setMessages((prev) => [...prev, applyMessage])
      }
    } catch (e) {
      console.log(e)
      const errorMessage: Message = {
        id: uuidv4(),
        status: 'error',
        type: 'bot',
        message: 'Oh oh, seems like something went wrong.',
      }
      setMessages((prev) => [...prev, errorMessage])
    }
  }

  const applyBookmark = async (bookmark: Bookmark) => {
    setActiveBookmark(bookmark)

    const appliedMessage: Message = {
      id: uuidv4(),
      status: 'info',
      type: 'bot',
      message: `Successfully applied bookmark '${bookmark?.name}'`,
    }
    setMessages((prev) => [...prev, appliedMessage])
  }

  const clearHistory = () => {
    setMessages([])
    setGeneratingBookmarkStatus(GeneratingBookmarkStatus.idle)
    sessionStorage.removeItem(`${organizationId}-${reportSlug}-bookmarks`)
  }

  useEffect(() => {
    const enterListener = (e) => {
      if (e.key === 'Enter') {
        sendMessage()
      }
    }

    if (messageInputRef.current !== null) {
      messageInputRef.current.addEventListener('keydown', enterListener)
    }

    return () => {
      messageInputRef.current?.removeEventListener('keydown', enterListener)
    }
  }, [messageInputRef, currentMessage])

  useEffect(() => {
    document.getElementById(messages[messages.length - 1]?.id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    })

    if (messages.length > 0) {
      sessionStorage.setItem(
        `${organizationId}-${reportSlug}-bookmarks`,
        JSON.stringify(messages)
      )
    }
  }, [messages, isChatOpen])

  useEffect(() => {
    if (bookmarkError) {
      const errorMessage: Message = {
        id: uuidv4(),
        status: 'error',
        type: 'bot',
        message: `Oh oh, seems like something went wrong\n\nError:\n${
          bookmarkError.message
        }\n\nDetail:\n${bookmarkError.type}${
          bookmarkError?.tip ? `\n\nTip:\n${bookmarkError.tip}` : ''
        }`,
      }
      setMessages((prev) => [...prev, errorMessage])
      setBookmarkError(null)
    }
  }, [bookmarkError])

  useEffect(() => {
    const storedHistory = sessionStorage.getItem(
      `${organizationId}-${reportSlug}-bookmarks`
    )
    if (storedHistory) {
      const parsedHistory = JSON.parse(storedHistory)
      setMessages(parsedHistory)
      setGeneratingBookmarkStatus(GeneratingBookmarkStatus.idle)
    }
  }, [])

  return (
    <div className="chat">
      <div
        className={`bubble ${!isChatOpen ? 'tooltip' : ''} top md`}
        onClick={openChat}
        data-tooltip="Chat"
      >
        <span className="material-icons-outlined non-selectable">
          {isChatOpen ? 'chat_bubble' : 'chat_bubble_outline'}
        </span>
      </div>
      {isChatOpen && (
        <>
          <div className={`chat-body ${isFullscreen ? 'fullscreen' : ''}`}>
            <div className="chat-messages">
              <div className="message bot">
                <p className="bot">Hello! Write a prompt to get started</p>
              </div>
              {messages.map((message) => {
                return (
                  <div
                    key={message.id}
                    id={message.id}
                    className={`message ${message.type}`}
                  >
                    <p className={`${message.type} ${message.status}`}>
                      {message.message}
                    </p>
                    {message.action === 'apply' && (
                      <div
                        className="message-action"
                        onClick={() => applyBookmark(message.bookmark!)}
                      >
                        Apply
                      </div>
                    )}
                  </div>
                )
              })}
              {generatingBookmarkStatus ===
                GeneratingBookmarkStatus.loading && (
                <ScreenLoading></ScreenLoading>
              )}
            </div>
            <div className="chat-bottom">
              <div className="chat-header">
                <h3>Message</h3>
                <div className="chat-header-actions">
                  <button onClick={clearHistory} className="clear">
                    Clear
                  </button>
                  <button
                    onClick={() => setIsFullscreen((prev) => !prev)}
                    className="expand"
                  >
                    <span className="material-icons-outlined non-selectable">
                      {!isFullscreen ? 'open_in_full' : 'close_fullscreen'}
                    </span>
                  </button>
                </div>
              </div>
              <div className="chat-input">
                <input
                  ref={messageInputRef}
                  placeholder="Type your desired visual"
                  type="text"
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                />
                <button
                  className={`send-message ${
                    currentMessage.length === 0 ? 'disabled' : 'tooltip top'
                  }`}
                  onClick={sendMessage}
                  disabled={currentMessage.length === 0}
                  data-tooltip="Send"
                >
                  <span className="material-icons-outlined non-selectable">
                    send
                  </span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default BookmarkChat
